import { analytics } from '@lib/analytics/page-events';
import type {
  ProductListV2,
  ProductMembership,
} from '@lib/types/api/ProductListV2';
import type { ListData } from 'ui-components';

import { addItemToList, removeItemFromList } from './productListsApi';

export type ProductInfo = { id: string; name: string };

export const toListData = (
  list: ProductListV2,
  personal: boolean = true
): ListData => ({
  id: list.id,
  name: list.name,
  count: list.productCount,
  type: list.visibility,
  personal,
});

export function getListChanges(
  initialListIds: string[],
  selectedLists: ListData[],
  availableListIds: ReadonlySet<string>
) {
  const initialSet = new Set(initialListIds);
  const selectedIdSet = new Set(selectedLists.map((l) => l.id));

  const listsToAdd = selectedLists.filter((l) => !initialSet.has(l.id));
  // Membership reads and list reads are eventually consistent. A membership
  // can reference a list that is absent from the current list response; because
  // the user could not see or deselect it, do not turn that stale mismatch into
  // a destructive remove request.
  const idsToRemove = initialListIds.filter(
    (id) => availableListIds.has(id) && !selectedIdSet.has(id)
  );

  return { listsToAdd, idsToRemove };
}

export function buildMembershipAfterSave(
  previousMembership: ProductMembership,
  selectedLists: ListData[],
  availableListIds: ReadonlySet<string>
): ProductMembership {
  const mergeUnavailable = (selectedIds: string[], previousIds: string[]) =>
    Array.from(
      new Set([
        ...selectedIds,
        ...previousIds.filter((id) => !availableListIds.has(id)),
      ])
    );

  return {
    personal: mergeUnavailable(
      selectedLists.filter((list) => list.personal).map((list) => list.id),
      previousMembership.personal
    ),
    organization: mergeUnavailable(
      selectedLists.filter((list) => !list.personal).map((list) => list.id),
      previousMembership.organization
    ),
  };
}

export async function syncListChanges({
  listsToAdd,
  idsToRemove,
  productId,
  getOrgIdForList,
}: {
  listsToAdd: ListData[];
  idsToRemove: string[];
  productId: string;
  getOrgIdForList: (listId: string) => string | undefined;
}): Promise<void> {
  await Promise.all([
    ...listsToAdd.map((l) =>
      addItemToList(l.id, productId, getOrgIdForList(l.id))
    ),
    ...idsToRemove.map((id) =>
      removeItemFromList(id, productId, getOrgIdForList(id))
    ),
  ]);
}

export type SaveToastContent = { title: string; listLabel: string };

export function buildSaveToastContent({
  addedNames,
  removedNames,
  microcopy,
}: {
  addedNames: string[];
  removedNames: string[];
  microcopy: { savedTo: string; removedFrom: string; multipleLists: string };
}): SaveToastContent | null {
  const format = (names: string[]) =>
    names.length === 1
      ? names[0]
      : microcopy.multipleLists.replace('{count}', String(names.length));

  if (addedNames.length > 0) {
    return { title: microcopy.savedTo, listLabel: format(addedNames) };
  }
  if (removedNames.length > 0) {
    return { title: microcopy.removedFrom, listLabel: format(removedNames) };
  }
  return null;
}

export function trackListChangesAnalytics({
  listsToAdd,
  idsToRemove,
  productId,
  productName,
}: {
  listsToAdd: ListData[];
  idsToRemove: string[];
  productId: string;
  productName: string;
}): void {
  listsToAdd.forEach(() =>
    analytics.pushSaveToListEvent({
      asset_id: productId,
      asset_name: productName,
    })
  );
  idsToRemove.forEach(() =>
    analytics.pushRemoveFromListEvent({
      asset_id: productId,
      asset_name: productName,
    })
  );
}
